import {useRef, useState} from 'react';
import {forgotPassword, resetPassword} from './useAuth';

type RequestOutcome =
  | {kind: 'success'}
  | {kind: 'ignored'}
  | {kind: 'error'; status?: number; thrown: boolean};

type RequestError = Extract<RequestOutcome, {kind: 'error'}>;

function useRecoveryRequest<Input>(request: (input: Input) => Promise<{error?: boolean; status?: number}>) {
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<RequestError | null>(null);

  const submit = async (input: Input): Promise<RequestOutcome> => {
    if (inFlight.current) return {kind: 'ignored'};

    inFlight.current = true;
    setPending(true);
    setError(null);

    try {
      const result = await request(input);
      if (result?.error) {
        const failure: RequestError = {kind: 'error', status: result.status, thrown: false};
        setError(failure);
        return failure;
      }
      return {kind: 'success'};
    } catch {
      const failure: RequestError = {kind: 'error', thrown: true};
      setError(failure);
      return failure;
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return {submit, pending, error};
}

export function useForgotPasswordRecovery() {
  return useRecoveryRequest(forgotPassword);
}

export function useResetPasswordRecovery() {
  return useRecoveryRequest(resetPassword);
}
